import { Component, ChangeDetectorRef, ViewChild, output, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25ModalModule } from "../../../s25-modal/s25.modal.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25Util } from "../../../../util/s25-util";
import { Budget } from "../Budget";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";
import { BudgetService } from "../budget.service";
import { Bind } from "../../../../decorators/bind.decorator";
import { WorkflowService } from "../../../workflow/service/workflow.service";
import { Workflow } from "../../../workflow/service/Workflow";

@TypeManagerDecorator("s25-ng-budget-edit-modal")
@Component({
    selector: "s25-ng-budget-edit-modal",
    template: `
        <s25-ng-modal #budgetEditModal [title]="labels.EDIT_ALLOCATION" [type]="'custom'" [size]="'lg'">
            <ng-template #s25ModalBody>
                @if (showBudgetForm) {
                    <form [formGroup]="budgetForm" class="budget-edit-form">
                        <div class="form-section">
                            <h4>{{ labels.ALLOCATION_DETAILS }}</h4>
                            <div class="form-field">
                                <label for="editBudgetName" class="required">{{ labels.ALLOCATION_NAME }}</label>
                                <input
                                    type="text"
                                    id="editBudgetName"
                                    class="c-input"
                                    formControlName="name"
                                    maxlength="100"
                                />
                            </div>
                            <div class="form-field">
                                <label for="editDescription">Description</label>
                                <textarea
                                    id="editDescription"
                                    class="c-input"
                                    formControlName="description"
                                    rows="3"
                                    maxlength="500"
                                ></textarea>
                            </div>
                            <div class="form-field">
                                <label for="editTotalAllocated" class="required">Allocated Amount</label>
                                <input
                                    type="number"
                                    id="editTotalAllocated"
                                    class="c-input"
                                    formControlName="totalAllocated"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                        </div>

                        <div class="form-section">
                            <h4>Approval Workflow</h4>
                            <div class="form-field">
                                <label for="editWorkflowProcess">Approval Workflow</label>
                                <select id="editWorkflowProcess" class="c-input" formControlName="workflowProcessId">
                                    <option [ngValue]="null">None (auto-approve plans)</option>
                                    @for (workflow of availableWorkflows; track workflow.workflowProcessId) {
                                        <option [ngValue]="workflow.workflowProcessId">
                                            {{ workflow.workflowProcessName }}
                                        </option>
                                    }
                                </select>
                                <p class="field-help">{{ labelPhrases.allocationWorkflowHelp }}</p>
                            </div>
                        </div>

                        <div class="form-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelForm"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'primary'"
                                [buttonText]="labels.SAVE_ALLOCATION"
                                [onClick]="onSubmit"
                                [isLoading]="isSubmitting"
                                [loadingText]="'Saving...'"
                                [disabled]="budgetForm.invalid"
                            ></s25-ng-button>
                        </div>
                    </form>
                }
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        .budget-edit-form {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .form-section {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 1.5rem;
            background: var(--color--page-bg);
        }

        .form-section h4 {
            margin: 0 0 1rem;
            color: var(--color--text);
            font-size: 1.1rem;
            font-weight: 600;
        }

        .form-field {
            display: flex;
            flex-direction: column;
            margin-bottom: 1rem;
        }

        .form-field:last-child {
            margin-bottom: 0;
        }

        .form-field label {
            font-weight: 600;
            margin-bottom: 0.5rem;
        }

        .form-field label.required::after {
            content: " *";
            color: var(--color--danger);
        }

        .field-help {
            margin: 0.5rem 0 0;
            color: var(--color--text-muted);
            font-size: 0.85rem;
            line-height: 1.4;
        }

        .form-actions {
            display: flex;
            gap: 1rem;
            justify-content: flex-end;
            padding-top: 1rem;
            border-top: 1px solid var(--color--border);
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25ModalModule, S25ButtonComponent, ReactiveFormsModule],
})
export class BudgetEditModalComponent {
    readonly labels = BudgetLabels;
    readonly labelPhrases = BudgetLabelPhrases;

    @ViewChild("budgetEditModal") budgetEditModal: any;

    readonly budgetUpdated = output<Budget.OrgBudget>();

    showBudgetForm = false;
    isSubmitting = false;
    budgetForm: FormGroup;
    availableWorkflows: Workflow.WorkflowProcess[] = [];
    private editingBudget: Budget.OrgBudget | null = null;
    private organizationId: number | null = null;

    constructor(
        private formBuilder: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.budgetForm = this.formBuilder.group({
            name: ["", [Validators.required, Validators.minLength(3)]],
            description: [""],
            totalAllocated: [0, [Validators.required, Validators.min(0.01)]],
            workflowProcessId: [null],
        });
    }

    async openEditModal(organizationId: number, budget: Budget.OrgBudget): Promise<void> {
        if (!this.availableWorkflows.length) {
            try {
                this.availableWorkflows = await WorkflowService.getWorkflowProcesses(true);
            } catch (error) {
                console.error("Error loading workflow processes:", error);
                this.availableWorkflows = [];
            }
        }

        this.organizationId = organizationId;
        this.editingBudget = budget;
        this.budgetForm.patchValue({
            name: budget.name,
            description: budget.description ?? "",
            totalAllocated: budget.totalAllocated,
            workflowProcessId: budget.workflowProcessId ?? null,
        });
        this.showBudgetForm = true;
        this.budgetEditModal?.open();
        this.changeDetectorRef.detectChanges();
    }

    @Bind
    async onSubmit(): Promise<void> {
        if (this.budgetForm.invalid || !this.editingBudget || this.organizationId == null) {
            return;
        }

        this.isSubmitting = true;
        this.changeDetectorRef.detectChanges();

        try {
            const formValue = this.budgetForm.value;
            const payload: Partial<Budget.OrgBudget> = {
                budgetId: this.editingBudget.budgetId,
                name: formValue.name,
                description: formValue.description,
                totalAllocated: formValue.totalAllocated,
                workflowProcessId: formValue.workflowProcessId ?? null,
            };

            const [_, error] = await S25Util.Maybe(BudgetService.updateOrgBudget(this.organizationId, payload));

            if (error) {
                throw error;
            }

            const updatedBudget: Budget.OrgBudget = {
                ...this.editingBudget,
                ...payload,
            };
            this.budgetUpdated.emit(updatedBudget);
            this.closeModal();
        } catch (error) {
            S25Util.showError(error, "Failed to update budget");
        } finally {
            this.isSubmitting = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    cancelForm = (): void => {
        this.closeModal();
    };

    private closeModal(): void {
        this.showBudgetForm = false;
        this.editingBudget = null;
        this.organizationId = null;
        this.budgetForm.reset();
        this.budgetEditModal?.close();
    }
}
